import { useEffect, useRef, useState } from 'react';
import type Reaptcha from 'reaptcha';
import useRouter from '~/client/hooks/router/useRouter';
import useAccountPopoverStore from '~/client/store/useAccountPopoverStore';
import deserializeErrors from '~/shared/utils/errors/deserializeErrors';
import useHandleLogin from '../../../hooks/useHandleLogin';
import useRedirectUrl from '../../../hooks/useRedirectUrl';
import hasCaptchaErrors from '../../../Shared/utils/hasCaptchaErrors';
import hasCredentialErrors from '../../../Shared/utils/hasCredentialErrors';
import hasOtpErrors from '../../../Shared/utils/hasOptErrors';
import hasSms2faError from '../../../Shared/utils/hasSms2faError';
import AuthStepsEnum from '../AuthStepsEnum';
import useStepByStepAuthContext from '../context/hooks/useStepByStepAuthContext';
import useLoginOrSignupSubmit from '../hooks/helpers/useLoginOrSignupSubmit';

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
};

const useEnterPasswordStep = ({ onSuccess, isPopover }: Props) => {
  const redirect = useRedirectUrl();
  const { reload, locale, replace } = useRouter();
  const { dismissPopover } = useAccountPopoverStore();
  const { socialAuthType } = useLoginOrSignupSubmit({});

  const [loading, setLoading] = useState(false);
  const [loginSuccessful, setLoginSuccessful] = useState(false);
  const {
    loginCaptchaRequired: captchaRequired,
    showInvalidCredentials,
    setStep,
    control,
    errors,
    required,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState,
    setIsStubSms2fa,
    setLoginCaptchaRequired,
    setShowInvalidCredentials,
  } = useStepByStepAuthContext();
  const recaptchaRef = useRef<Reaptcha>(null);

  const isSubmitDisabled = !formState?.isValid || !formState.isDirty;

  const { handleLogin } = useHandleLogin({ captchaRequired });
  useEffect(() => {
    if (socialAuthType) {
      setStep(AuthStepsEnum.EnterSms2fa);
    }
    if (showInvalidCredentials) {
      clearErrors?.();
    }
    setIsStubSms2fa(false);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (loginSuccessful) {
      isPopover ? reload() : replace(redirect);
    }
  }, [locale, isPopover, loginSuccessful, replace, redirect, reload]);

  return {
    control,
    required,
    errors,
    captchaRequired,
    recaptchaRef,
    loading,
    isSubmitDisabled,
    showInvalidCredentials,
    handleCreateAccount: () => setStep(AuthStepsEnum.FinishSignUp),
    setShowInvalidCredentials,
    handleSubmit: handleSubmit(async (values) => {
      setLoading(true);
      clearErrors?.();
      const payload = await handleLogin({
        ...values,
      });
      if (payload.errors?.length) {
        setLoading(false);
        const deserializedErrors = deserializeErrors(
          payload.errors,
        ) as DeserializeError;
        const message = payload.errors[0].message;
        if (hasCredentialErrors(deserializedErrors)) {
          setIsStubSms2fa(true);
          setValue?.('captcha', '');
          setShowInvalidCredentials(true);
          return;
        }
        if (hasCaptchaErrors(deserializedErrors)) {
          recaptchaRef.current?.reset();
          setLoginCaptchaRequired(true);
          return;
        }
        if (hasSms2faError(deserializedErrors)) {
          setValue?.('captcha', '');
          setStep(AuthStepsEnum.EnterSms2fa);
          return;
        }
        if (hasOtpErrors(deserializedErrors)) {
          setValue?.('captcha', '');
          setValue?.('uses2FA', true);
          setStep(AuthStepsEnum.EnterSms2fa);
          return;
        }

        setError?.('global', { message });
        return;
      }
      onSuccess?.();
      if (isPopover) {
        dismissPopover(true);
      }
      setLoginSuccessful(true);
    }),
  };
};

export default useEnterPasswordStep;
